import type { ProjectDataLLPEvent } from '@avisoma//shared';

type LLPPartEvent = ProjectDataLLPEvent['parts'][number]['events'][number];

interface PartEventsTimelineProps {
  events: LLPPartEvent[];
}

const META_FIELDS: [keyof LLPPartEvent & string, string][] = [
  ['esn', 'ESN'],
  ['tsn', 'TSN'],
  ['csn', 'CSN'],
  ['msn', 'MSN'],
  ['reason', 'Reason'],
  ['thrust', 'Thrust'],
  ['operator', 'Operator'],
  ['aircraftReg', 'Aircraft Reg'],
];

function byDateDesc(a: LLPPartEvent, b: LLPPartEvent) {
  // ISO-ish date strings compare lexicographically; undated events sink to the bottom.
  return (b.date ?? '').localeCompare(a.date ?? '');
}

export function PartEventsTimeline({ events }: PartEventsTimelineProps) {
  const sorted = [...events].sort(byDateDesc);

  return (
    <ol className="space-y-0">
      {sorted.map((event, index) => {
        const meta = META_FIELDS.filter(([key]) => {
          const value = event[key];
          return value !== null && value !== undefined && value !== '';
        });
        return (
          <li key={index} className="relative flex gap-4 pb-6 last:pb-0">
            <div className="flex flex-col items-center">
              <span className="mt-1.5 size-2.5 shrink-0 rounded-full bg-blue-500 ring-4 ring-blue-100 dark:ring-blue-500/20" />
              {index < sorted.length - 1 && (
                <span className="mt-1 w-px flex-1 bg-gray-200 dark:bg-zinc-700" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                <span className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                  {event.type}
                </span>
                <span className="text-sm text-gray-500 dark:text-zinc-400">
                  {event.date ?? 'no date'}
                </span>
              </div>
              {meta.length > 0 && (
                <dl className="mt-1 grid grid-cols-2 gap-x-6 gap-y-0.5 sm:grid-cols-3">
                  {meta.map(([key, label]) => (
                    <div key={key} className="flex items-baseline gap-1.5 text-xs">
                      <dt className="shrink-0 text-gray-400 dark:text-zinc-500">{label}</dt>
                      <dd className="truncate text-gray-700 dark:text-zinc-300">
                        {String(event[key])}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
